import { motion } from "framer-motion";
import { Scale, Calculator, PackageCheck, ArrowUpRight } from "lucide-react";
import { BOOKING_URL } from "@/lib/constants";

const STEPS = [
  "You refer a client with 2025 imports",
  "We audit and file their CAPE claim",
  "Client recovers their IEEPA tariff refund",
  "You earn a referral fee on recovery",
];

const PARTNERS = [
  {
    icon: PackageCheck,
    title: "Customs Brokers",
    body: "Direct access to importing clients — you already know who paid tariffs.",
  },
  {
    icon: Scale,
    title: "Trade Attorneys",
    body: "Complement your legal work with tangible financial recovery for clients.",
  },
  {
    icon: Calculator,
    title: "CPAs & Advisors",
    body: "IEEPA refunds are material. Add real value and a fee for the introduction.",
  },
];

export default function Referral() {
  return (
    <section
      id="referral"
      data-testid="referral-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#0A1936]" />
              <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
                07 · Referral Program
              </span>
            </div>
            <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Turn your network
              <br />
              <span className="italic">into a revenue stream.</span>
            </h2>

            <ol className="mt-10 space-y-4">
              {STEPS.map((s, i) => (
                <motion.li
                  key={s}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="flex items-start gap-4"
                >
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full border border-[#F5B800] text-[#F5B800] flex items-center justify-center font-mono text-xs">
                    {i + 1}
                  </span>
                  <span className="text-[#0A1936]/80 text-base">{s}</span>
                </motion.li>
              ))}
            </ol>

            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="referral-cta"
              className="btn-magnetic mt-10 inline-flex items-center gap-3 border border-[#0A1936] hover:bg-[#0A1936] hover:text-white text-[#0A1936] font-semibold text-sm px-6 py-4 rounded-sm transition-colors"
            >
              Become a Partner
              <ArrowUpRight size={16} />
            </a>
          </motion.div>

          <div className="lg:col-span-7 space-y-4">
            <div className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/60 mb-4">
              Who makes a great partner?
            </div>
            {PARTNERS.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`referral-partner-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                whileHover={{ x: 6 }}
                className="group flex items-center gap-6 p-6 lg:p-8 border-b border-[#0A1936]/10 hover:border-[#F5B800] transition-colors"
              >
                <div className="shrink-0 w-14 h-14 rounded-sm border border-[#0A1936]/15 group-hover:bg-[#0A1936] group-hover:border-[#0A1936] transition-colors flex items-center justify-center">
                  <p.icon
                    size={22}
                    className="text-[#0A1936] group-hover:text-[#F5B800] transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl text-[#0A1936] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-[#0A1936]/70 text-sm leading-relaxed">
                    {p.body}
                  </p>
                </div>
                <ArrowUpRight
                  size={22}
                  className="shrink-0 text-[#0A1936]/30 group-hover:text-[#F5B800] transition-colors"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
